import { useState, useCallback } from 'react';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

const API = APP_CONFIG.BACKEND_URL;

export interface SharedRecipient {
  recipient_id: string;
  nombre: string;
  apellido: string;
  email: string;
  departamento: string;
  created_at: string;
}

export interface SharedByMeItem {
  id: string;
  titulo: string;
  preview: string;
  message_count: number;
  fecha_creacion: string;
  fecha_actualizacion: string;
  es_favorita: boolean;
  shared_with: SharedRecipient[];
}

export interface SharedWithMeItem {
  id: string;
  titulo: string;
  preview: string;
  message_count: number;
  fecha_creacion: string;
  fecha_actualizacion: string;
  share_favorita: boolean;
  shared_at: string;
  owner_id: string;
  owner_nombre: string;
  owner_apellido: string;
  owner_email: string;
  owner_departamento: string;
}

export interface TeamUser {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  departamento: string;
  estado: string;
}

export function useSharing(userId: string) {
  const [sharedByMe, setSharedByMe] = useState<SharedByMeItem[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<SharedWithMeItem[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(false);

  const loadSharedByMe = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/api/v1/shares/by-me?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setSharedByMe(data);
      }
    } catch (err) {
      console.error('Error loading shared by me:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadSharedWithMe = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/api/v1/shares/with-me?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setSharedWithMe(data);
      }
    } catch (err) {
      console.error('Error loading shared with me:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadTeamUsers = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/v1/users`);
      if (res.ok) {
        const data = await res.json();
        const active = (data as TeamUser[]).filter(
          (u) => u.estado === 'activo' && u.id !== userId
        );
        setTeamUsers(active);
      }
    } catch (err) {
      console.error('Error loading team users:', err);
    }
  }, [userId]);

  const shareConversation = useCallback(
    async (conversationId: string, recipientIds: string[]) => {
      try {
        const res = await authFetch(`${API}/api/v1/shares`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversation_id: conversationId,
            owner_id: userId,
            recipient_ids: recipientIds,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: 'Error al compartir' }));
          throw new Error(err.detail || 'Error al compartir');
        }
        return await res.json();
      } catch (err) {
        console.error('Error sharing conversation:', err);
        throw err;
      }
    },
    [userId]
  );

  const revokeShare = useCallback(
    async (conversationId: string, recipientId: string) => {
      try {
        await authFetch(
          `${API}/api/v1/shares/${conversationId}?owner_id=${userId}&recipient_id=${recipientId}`,
          { method: 'DELETE' }
        );
      } catch (err) {
        console.error('Error revoking share:', err);
      }
    },
    [userId]
  );

  const toggleShareFavorite = useCallback(
    async (conversationId: string) => {
      try {
        const res = await authFetch(
          `${API}/api/v1/shares/${conversationId}/favorite?user_id=${userId}`,
          { method: 'POST' }
        );
        if (res.ok) {
          const data = await res.json();
          setSharedWithMe((prev) =>
            prev.map((item) =>
              item.id === conversationId
                ? { ...item, share_favorita: data.es_favorita }
                : item
            )
          );
          return data.es_favorita;
        }
      } catch (err) {
        console.error('Error toggling share favorite:', err);
      }
      return false;
    },
    [userId]
  );

  const getShareRecipients = useCallback(
    async (conversationId: string): Promise<string[]> => {
      try {
        const res = await authFetch(
          `${API}/api/v1/shares/recipients/${conversationId}?owner_id=${userId}`
        );
        if (res.ok) return await res.json();
      } catch (err) {
        console.error('Error getting share recipients:', err);
      }
      return [];
    },
    [userId]
  );

  const viewSharedConversation = useCallback(
    async (conversationId: string) => {
      try {
        const res = await authFetch(
          `${API}/api/v1/shares/${conversationId}/view?user_id=${userId}`
        );
        if (!res.ok) return null;
        return await res.json();
      } catch (err) {
        console.error('Error viewing shared conversation:', err);
        return null;
      }
    },
    [userId]
  );

  return {
    sharedByMe,
    sharedWithMe,
    teamUsers,
    loading,
    loadSharedByMe,
    loadSharedWithMe,
    loadTeamUsers,
    shareConversation,
    revokeShare,
    toggleShareFavorite,
    getShareRecipients,
    viewSharedConversation,
  };
}
